//@ts-nocheck

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MediaItem, MediaFile } from '../types';
import { doc, updateDoc, getFirestore, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { MediaDisplay } from './MediaDisplay';
import { CarouselNavigation } from './CarouselNavigation';
import PostVideoTrimmer from './PostVideoTrimmer';

interface ModalProps {
  item: MediaItem | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

const Modal: React.FC<ModalProps> = ({ item, isOpen, onClose, onUpdate }) => {
  const { currentUser } = useAuth();
  const [showTrimmer, setShowTrimmer] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Trim timestamps (managed by PostVideoTrimmer)
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(10);
  const [updating, setUpdating] = useState(false);
  const [caption, setCaption] = useState(item?.caption || '');
  const captionRef = useRef<HTMLTextAreaElement>(null);
  const [deletedSlides, setDeletedSlides] = useState<number[]>([]);
  const [capturedFrames, setCapturedFrames] = useState<MediaFile[]>([]);

  // Reset slide and caption when modal opens/closes
  useEffect(() => {
    if (isOpen && item) {
      setCurrentSlide(0);
      setCaption(item.caption || '');
      setStartTime(item.startAt || 0);
      setEndTime(item.endAt || 10);
      setShowTrimmer(false);
      setDeletedSlides([]);
      setCapturedFrames([]);
    } else if (!isOpen) {
      // Reset state when closing
      setCurrentSlide(0);
      setCaption('');
      setStartTime(0);
      setEndTime(10);
      setShowTrimmer(false);
      setUpdating(false);
      setDeletedSlides([]);
      setCapturedFrames([]);
    }
  }, [isOpen, item]);

  // Handle keyboard navigation for carousel
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen || !item) return;

      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        handlePrevSlide();
      } else if (event.key === 'ArrowRight') {
        handleNextSlide();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, item, currentSlide]);

  // Early return after all hooks
  if (!isOpen || !item) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrevSlide = () => {
    if (!item || !item.media || item.media.length <= 1) return;

    let newSlide = currentSlide - 1;
    if (newSlide < 0) newSlide = item.media.length - 1;

    // Skip deleted slides
    while (deletedSlides.includes(newSlide) && newSlide !== currentSlide) {
      newSlide--;
      if (newSlide < 0) newSlide = item.media.length - 1;
    }

    if (newSlide !== currentSlide) {
      setCurrentSlide(newSlide);
    }
  };

  const handleNextSlide = () => {
    if (!item || !item.media || item.media.length <= 1) return;

    let newSlide = currentSlide + 1;
    if (newSlide >= item.media.length) newSlide = 0;

    // Skip deleted slides
    while (deletedSlides.includes(newSlide) && newSlide !== currentSlide) {
      newSlide++;
      if (newSlide >= item.media.length) newSlide = 0;
    }

    if (newSlide !== currentSlide) {
      setCurrentSlide(newSlide);
    }
  };

  // Get current media item being displayed
  const getCurrentMedia = (): MediaFile | null => {
    if (!item.media || item.media.length === 0) return null;

    // For Video posts, always show the video (not the thumbnail)
    if (item.type === 'Video') {
      return item.media.find(media => media.type === 'video') || item.media[0];
    }

    // Skip deleted slides
    if (deletedSlides.includes(currentSlide)) {
      const nextValidSlide = item.media.findIndex((_, index) => !deletedSlides.includes(index));
      if (nextValidSlide !== -1 && nextValidSlide !== currentSlide) {
        setCurrentSlide(nextValidSlide);
      }
      return null;
    }

    return item.media[currentSlide] || null;
  };

  const currentMedia = getCurrentMedia();
  const activeMediaCount = item.media ? item.media.filter((_, index) => !deletedSlides.includes(index)).length : 0;
  const isCarousel = item.type === 'Sidecar' && activeMediaCount > 1;
  const isVideoPost = item.type === 'Video';

  // Get video media for trimming (only for Video type posts)
  const getVideoMedia = (): MediaFile | null => {
    if (!item.media || item.media.length === 0) return null;
    return item.media.find(media => media.type === 'video') || null;
  };

  const videoMedia = getVideoMedia();

  // Debug logging
  console.log('Modal render:', {
    itemType: item.type,
    mediaCount: item.media?.length,
    currentSlide,
    currentMedia,
    isCarousel,
    isVideoPost,
    videoMedia
  });

  const handleTrimSave = (startAt: number, endAt: number, frames?: MediaFile[]) => {
    // Update local state
    setStartTime(startAt);
    setEndTime(endAt);
    if (frames && frames.length > 0) {
      setCapturedFrames(frames);
    }
    setShowTrimmer(false);
    // The actual save to Firestore will happen when user clicks "Save Changes"
  };

  const handleApprove = async () => {
    if (updating) return;

    setUpdating(true);
    try {
      // Use ref value to ensure we get the latest caption even if onChange hasn't completed
      const latestCaption = captionRef.current?.value || caption;

      const updateData: any = {
        status: 'approved',
        caption: latestCaption,
      };

      // Save trim timestamps if they exist
      if (startTime > 0 || endTime > 0) {
        updateData.startAt = startTime;
        updateData.endAt = endTime;
      }

      // Handle captured frames - convert Video to Sidecar if frames are captured
      if (capturedFrames.length > 0 && item.type === 'Video') {
        // Convert to Sidecar type
        updateData.type = 'Sidecar';
        
        // Build childPosts array with captured images and the original video
        const childPosts = capturedFrames.map((frame, index) => ({
          type: 'image',
          displayUrl: frame.downloadURL,
          url: frame.downloadURL,
        }));
        
        // Add the video as the last child
        const videoMedia = item.media?.find(media => media.type === 'video');
        if (videoMedia) {
          childPosts.push({
            type: 'Video',
            videoUrl: videoMedia.downloadURL,
            displayUrl: item.media?.find(media => media.type === 'image')?.downloadURL || item.url,
          });
        }
        
        updateData.childPosts = childPosts;
        console.log('Converting Video to Sidecar with captured frames:', childPosts);
      }

      // Apply deleted slides if any (for Sidecar posts)
      if (deletedSlides.length > 0 && item.type === 'Sidecar' && item.childPosts) {
        const newChildPosts = item.childPosts.filter((_, index) => !deletedSlides.includes(index));
        updateData.childPosts = newChildPosts;
      }

      console.log('Approving post:', item.id, 'Path:', item.docPath, 'Data:', updateData);

      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const db = getFirestore();

      // Update the IGPost document
      if (!item.docPath) {
        throw new Error('Document path not found');
      }

      const docRef = doc(db, item.docPath);
      console.log('Updating document at path:', item.docPath);
      await updateDoc(docRef, updateData);

      console.log('Post approved successfully');

      if (onUpdate) {
        onUpdate();
      }

      onClose();

    } catch (error) {
      console.error('Error approving post:', error);
      alert('Failed to approve post. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteChild = () => {
    if (item.type !== 'Sidecar' || !item.media) return;

    const remainingSlides = item.media.filter((_, index) => !deletedSlides.includes(index));
    if (remainingSlides.length <= 1) return;

    // Add current slide to deleted slides
    setDeletedSlides([...deletedSlides, currentSlide]);

    // Move to next available slide
    const nextValidSlide = item.media.findIndex((_, index) =>
      index !== currentSlide && !deletedSlides.includes(index)
    );

    if (nextValidSlide !== -1) {
      setCurrentSlide(nextValidSlide);
    }
  };

  const handleUndoDelete = (slideIndex: number) => {
    setDeletedSlides(deletedSlides.filter(index => index !== slideIndex));
  };

  const handleReject = async () => {
    if (updating) return;

    setUpdating(true);
    try {
      const updateData: any = {
        status: 'rejected'
      };

      // Apply deleted slides if any (for Sidecar posts)
      if (deletedSlides.length > 0 && item.type === 'Sidecar' && item.childPosts) {
        const newChildPosts = item.childPosts.filter((_, index) => !deletedSlides.includes(index));
        updateData.childPosts = newChildPosts;
      }

      console.log('Rejecting post:', item.id, 'Path:', item.docPath, 'Data:', updateData);

      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const db = getFirestore();

      // Update the IGPost document
      if (!item.docPath) {
        throw new Error('Document path not found');
      }

      const docRef = doc(db, item.docPath);
      console.log('Updating document at path:', item.docPath);
      await updateDoc(docRef, updateData);

      console.log('Post rejected successfully');

      if (onUpdate) {
        onUpdate();
      }

      onClose();

    } catch (error) {
      console.error('Error rejecting post:', error);
      alert('Failed to reject post. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full h-full max-w-4xl overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 z-50"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Carousel indicator */}
        {isCarousel && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 flex flex-col items-center gap-2">
            <div className="text-white bg-black bg-opacity-70 px-4 py-2 rounded-full text-sm font-medium">
              {currentSlide + 1} / {activeMediaCount}
            </div>
            {deletedSlides.length > 0 && (
              <div className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {deletedSlides.length} will be deleted
              </div>
            )}
          </div>
        )}
        
        {/* Captured frames indicator */}
        {capturedFrames.length > 0 && (
          <div className="absolute top-4 left-4 z-50">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              </svg>
              {capturedFrames.length} frame{capturedFrames.length > 1 ? 's' : ''} captured
            </div>
          </div>
        )}

        {/* Media Section - Full Screen Background */}
        <div className="absolute inset-0 bg-black">
          <div className="relative h-full">
            {/* Render current media (hidden when trimmer is active) */}
            {!showTrimmer && (
              <div className="relative h-full">
                <MediaDisplay
                  media={currentMedia}
                  fallbackUrl={item.url}
                  itemName={item.name}
                  mediaDownloaded={item.mediaDownloaded}
                />
                {/* Delete/Restore button overlay for Sidecar posts */}
                {item.type === 'Sidecar' && activeMediaCount > 0 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
                    {!deletedSlides.includes(currentSlide) ? (
                      <button
                        onClick={handleDeleteChild}
                        disabled={updating || activeMediaCount <= 1}
                        className={`font-medium py-3 px-6 rounded-full transition-all flex items-center gap-2 shadow-2xl ${updating || activeMediaCount <= 1
                          ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                          : 'bg-red-600 hover:bg-red-700 text-white hover:scale-110'
                          }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUndoDelete(currentSlide)}
                        className="font-medium py-3 px-6 rounded-full transition-all flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white shadow-2xl hover:scale-110"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}

                {/* Deleted slide overlay */}
                {deletedSlides.includes(currentSlide) && (
                  <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-10 pointer-events-none">
                    <div className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-2xl transform rotate-[-15deg]">
                      <div className="flex items-center gap-2">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span className="font-bold text-lg">WILL BE DELETED</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Trimmer UI (inline) */}
            {showTrimmer && isVideoPost && (
              <PostVideoTrimmer
                item={item}
                onSave={handleTrimSave}
                onClose={() => setShowTrimmer(false)}
              />
            )}

            {/* Carousel Navigation - only show if not trimming */}
            {isCarousel && !showTrimmer && (
              <CarouselNavigation
                mediaCount={item.media.length}
                currentSlide={currentSlide}
                onPrev={handlePrevSlide}
                onNext={handleNextSlide}
                onSelectSlide={(index) => {
                  // Allow navigation to deleted slides so user can see what they marked
                  setCurrentSlide(index);
                }}
              />
            )}

            {/* Trim/Capture button for Video posts only */}
            {isVideoPost && !showTrimmer && item.mediaDownloaded && (
              <button
                onClick={() => setShowTrimmer(true)}
                className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm flex items-center gap-1 z-40"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Trim / Capture
              </button>
            )}
          </div>

          {/* Scrollable Content Section - Overlaying */}
          <div className="bg-white rounded-t-3xl shadow-2xl z-30 flex flex-col" style={{ marginTop: '-14px', minHeight: '60vh', maxHeight: '100vh' }}>
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4 flex-shrink-0"></div>
            <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', minHeight: '0' }}>



              {/* Item Details */}
              <div className="p-4">
                {/* Caption Editor */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Caption
                  </label>
                  <textarea
                    ref={captionRef}
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    rows={8}
                    placeholder="Add a caption..."
                  />
                </div>


                {/* Captured frames summary */}
                {capturedFrames.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-blue-800 font-semibold text-sm">📸 {capturedFrames.length} frame{capturedFrames.length > 1 ? 's' : ''} captured</p>
                        <p className="text-blue-600 text-xs mt-1">This video will become a sidecar with {capturedFrames.length + 1} media item{capturedFrames.length > 0 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {capturedFrames.map((frame, index) => (
                        <div key={index} className="relative">
                          <img
                            src={frame.downloadURL}
                            alt={`Captured frame ${index + 1}`}
                            className="w-16 h-16 object-cover rounded border-2 border-blue-300"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Deleted slides summary */}
                {deletedSlides.length > 0 && (
                  <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-red-800 font-semibold text-sm">⚠️ {deletedSlides.length} slide{deletedSlides.length > 1 ? 's' : ''} marked for deletion</p>
                        <p className="text-red-600 text-xs mt-1">Changes will be applied when you approve/reject this post</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {deletedSlides.map(slideIndex => (
                        <button
                          key={slideIndex}
                          onClick={() => {
                            handleUndoDelete(slideIndex);
                            setCurrentSlide(slideIndex);
                          }}
                          className="px-3 py-1 bg-white border border-red-300 rounded-full text-xs text-red-700 hover:bg-red-100 transition-colors flex items-center gap-1"
                        >
                          <span>Slide {slideIndex + 1}</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approve/Reject Buttons - Hidden when trimming */}
                {!showTrimmer && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex gap-3">
                      <button
                        onClick={handleReject}
                        disabled={updating}
                        className={`flex-1 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${updating
                          ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                          : 'bg-gray-600 hover:bg-gray-700 text-white'
                          }`}
                      >
                        {updating ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Rejecting...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Reject
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleApprove}
                        disabled={updating}
                        className={`flex-1 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${updating
                          ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                      >
                        {updating ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Approving...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approve
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
